import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  CreditCard,
  Lock,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { paymentsApi } from "../lib/api";
import { PoweredBy } from "../components/PoweredBy";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PayInvoice() {
  const { invoiceId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [invoice, setInvoice] = useState(null); // null=loading | object | false=error
  const [errorMsg, setErrorMsg] = useState("");
  const [mode, setMode] = useState("full"); // full | partial
  const [partial, setPartial] = useState("");
  const [starting, setStarting] = useState(false);

  // Polling state (after Stripe redirect)
  const [polling, setPolling] = useState(!!sessionId);
  const [payResult, setPayResult] = useState(null); // { payment_status, invoice }
  const pollAttempts = useRef(0);

  const loadInvoice = useCallback(async () => {
    try {
      const inv = await paymentsApi.getInvoice(invoiceId);
      setInvoice(inv);
      // Staff popup can pre-set the amount via ?amount= (partial payment).
      const requested = parseFloat(params.get("amount") || "0");
      if (requested > 0 && requested < Number(inv.balanceDue || 0) - 0.005) {
        setMode("partial");
        setPartial(requested.toFixed(2));
      } else {
        setPartial(String(inv.balanceDue || ""));
      }
    } catch (err) {
      setInvoice(false);
      setErrorMsg(err?.response?.data?.detail || "We couldn't find this invoice.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  // Poll Stripe payment status when returning from checkout.
  const poll = useCallback(async () => {
    const MAX = 8;
    try {
      const res = await paymentsApi.status(sessionId);
      if (res.payment_status === "paid") {
        setPayResult(res);
        setPolling(false);
        setInvoice(res.invoice ? { ...(invoice || {}), ...res.invoice } : invoice);
        return;
      }
      if (res.status === "expired") {
        setPayResult({ payment_status: "expired" });
        setPolling(false);
        return;
      }
    } catch (_) {
      /* keep trying */
    }
    pollAttempts.current += 1;
    if (pollAttempts.current >= MAX) {
      setPayResult({ payment_status: "timeout" });
      setPolling(false);
      return;
    }
    setTimeout(poll, 2000);
  }, [sessionId, invoice]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  useEffect(() => {
    if (sessionId) {
      pollAttempts.current = 0;
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const startPayment = async () => {
    setStarting(true);
    setErrorMsg("");
    try {
      let amount = null;
      if (mode === "partial") {
        amount = parseFloat(partial);
        if (!amount || amount < (invoice.minPayment || 0.5)) {
          setErrorMsg(`Minimum payment is ${money(invoice.minPayment || 0.5)}.`);
          setStarting(false);
          return;
        }
      }
      const res = await paymentsApi.createCheckout(invoiceId, window.location.origin, amount);
      window.location.href = res.url; // redirect to Stripe hosted checkout
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Could not start the payment. Please try again.");
      setStarting(false);
    }
  };

  // When the staff payment popup opened this page in a new tab, auto-close it
  // after success — the popup back in the app updates the records live.
  useEffect(() => {
    if (payResult?.payment_status === "paid" && window.opener) {
      const t = setTimeout(() => window.close(), 3000);
      return () => clearTimeout(t);
    }
  }, [payResult]);

  const accent = invoice && invoice.company?.accentColor ? invoice.company.accentColor : "#2563EB";
  const logoUrl =
    invoice && invoice.company?.logoDataUrl
      ? invoice.company.logoDataUrl.startsWith("http")
        ? invoice.company.logoDataUrl
        : `${BACKEND}${invoice.company.logoDataUrl}`
      : null;

  return (
    <div className="min-h-screen bg-[#0B1E3F] flex items-center justify-center p-4" data-testid="pay-page">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="flex items-center justify-center gap-2.5 mb-5">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-9 max-w-[160px] object-contain" />
          ) : (
            <>
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center">
                <Wrench className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="text-white font-bold text-[17px] tracking-tight">
                {invoice?.company?.name || "ServicePro"}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Loading */}
          {invoice === null && (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin mb-2" />
              <div className="text-[13px]">Loading invoice…</div>
            </div>
          )}

          {/* Error */}
          {invoice === false && (
            <div className="py-16 px-6 text-center" data-testid="pay-error">
              <div className="h-12 w-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="h-6 w-6 text-rose-500" />
              </div>
              <div className="text-[15px] font-semibold text-slate-900">Invoice unavailable</div>
              <div className="text-[13px] text-slate-500 mt-1">{errorMsg}</div>
            </div>
          )}

          {invoice && invoice !== false && (
            <>
              {/* Polling after redirect */}
              {polling && (
                <div className="py-20 flex flex-col items-center justify-center text-slate-500" data-testid="pay-polling">
                  <Loader2 className="h-7 w-7 animate-spin mb-3" style={{ color: accent }} />
                  <div className="text-[14px] font-semibold text-slate-800">Confirming your payment…</div>
                  <div className="text-[12px] text-slate-400 mt-1">This only takes a moment.</div>
                </div>
              )}

              {/* Success */}
              {!polling && payResult?.payment_status === "paid" && (
                <div className="py-12 px-6 text-center" data-testid="pay-success">
                  <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                  <div className="text-[18px] font-bold text-slate-900">Payment received</div>
                  <div className="text-[13px] text-slate-500 mt-1">
                    Thank you! Your payment for invoice <strong>{invoice.id}</strong> was successful.
                  </div>
                  <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left">
                    <Row label="Invoice total" value={money(payResult.invoice?.total ?? invoice.total)} />
                    <Row label="Amount paid to date" value={money(payResult.invoice?.amountPaid ?? invoice.amountPaid)} />
                    <Row
                      label="Remaining balance"
                      value={money(payResult.invoice?.balanceDue ?? invoice.balanceDue)}
                      strong
                    />
                    <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between">
                      <span className="text-[12px] text-slate-500">Status</span>
                      <span className="text-[12px] font-bold" style={{ color: accent }}>
                        {payResult.invoice?.status ?? invoice.status}
                      </span>
                    </div>
                  </div>
                  {window.opener && (
                    <div className="mt-4 text-[12px] text-slate-400" data-testid="pay-success-close-hint">
                      This tab will close automatically — the records are already updated.
                    </div>
                  )}
                </div>
              )}

              {/* Expired / timeout */}
              {!polling && payResult && payResult.payment_status !== "paid" && (
                <div className="py-12 px-6 text-center" data-testid="pay-failed">
                  <div className="h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                    <AlertTriangle className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="text-[16px] font-bold text-slate-900">
                    {payResult.payment_status === "expired" ? "Payment session expired" : "Still processing"}
                  </div>
                  <div className="text-[13px] text-slate-500 mt-1">
                    {payResult.payment_status === "expired"
                      ? "Your checkout session expired before completing. You can try again below."
                      : "We couldn't confirm the payment yet. If you completed it, your invoice will update shortly."}
                  </div>
                  <Button
                    onClick={() => {
                      setPayResult(null);
                      window.location.href = `/pay/${invoiceId}`;
                    }}
                    className="mt-4 h-10 text-white text-[13px] font-semibold"
                    style={{ background: accent }}
                    data-testid="pay-retry"
                  >
                    Try again
                  </Button>
                </div>
              )}

              {/* Pay form */}
              {!polling && !payResult && (
                <>
                  <div className="px-6 pt-6 pb-4 border-b border-slate-100">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                      Invoice {invoice.id}
                    </div>
                    <div className="text-[15px] font-semibold text-slate-900 mt-0.5">{invoice.customer}</div>
                    {invoice.balanceDue <= 0 ? (
                      <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 text-[12.5px] font-semibold border border-emerald-200">
                        <CheckCircle2 className="h-4 w-4" /> Paid in full — nothing due
                      </div>
                    ) : (
                      <div className="mt-3 flex items-end justify-between">
                        <span className="text-[12.5px] text-slate-500">Balance due</span>
                        <span className="text-[28px] font-bold text-slate-900 leading-none">
                          {money(invoice.balanceDue)}
                        </span>
                      </div>
                    )}
                  </div>

                  {invoice.lineItems?.length > 0 && (
                    <div className="px-6 py-3 border-b border-slate-100 max-h-[180px] overflow-y-auto">
                      {invoice.lineItems.map((li, i) => (
                        <div key={i} className="flex items-center justify-between py-1 text-[12.5px]">
                          <span className="text-slate-600 truncate pr-3">
                            {li.description} {li.qty > 1 ? `× ${li.qty}` : ""}
                          </span>
                          <span className="text-slate-800 font-medium shrink-0">{money(li.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {invoice.balanceDue > 0 && invoice.onlinePaymentEnabled && (
                    <div className="px-6 py-5 space-y-3">
                      {/* Amount selector */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setMode("full")}
                          data-testid="pay-mode-full"
                          className={`h-10 rounded-lg border text-[13px] font-semibold transition-colors ${
                            mode === "full"
                              ? "border-transparent text-white"
                              : "border-slate-200 text-slate-700 hover:border-slate-300"
                          }`}
                          style={mode === "full" ? { background: accent } : {}}
                        >
                          Pay full balance
                        </button>
                        <button
                          onClick={() => setMode("partial")}
                          data-testid="pay-mode-partial"
                          className={`h-10 rounded-lg border text-[13px] font-semibold transition-colors ${
                            mode === "partial"
                              ? "border-transparent text-white"
                              : "border-slate-200 text-slate-700 hover:border-slate-300"
                          }`}
                          style={mode === "partial" ? { background: accent } : {}}
                        >
                          Pay partial amount
                        </button>
                      </div>

                      {mode === "partial" && (
                        <div>
                          <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                            Amount to pay (max {money(invoice.balanceDue)})
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[14px]">$</span>
                            <input
                              type="number"
                              min={invoice.minPayment || 0.5}
                              max={invoice.balanceDue}
                              step="0.01"
                              value={partial}
                              onChange={(e) => setPartial(e.target.value)}
                              data-testid="pay-partial-input"
                              className="w-full h-11 pl-7 pr-3 rounded-lg border border-slate-200 text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                            />
                          </div>
                        </div>
                      )}

                      {errorMsg && (
                        <div data-testid="pay-form-error" className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700">
                          {errorMsg}
                        </div>
                      )}

                      <Button
                        onClick={startPayment}
                        disabled={starting}
                        data-testid="pay-now-btn"
                        className="w-full h-12 text-white text-[14px] font-bold rounded-lg"
                        style={{ background: accent }}
                      >
                        {starting ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting to checkout…</>
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Pay {money(mode === "partial" ? parseFloat(partial || 0) : invoice.balanceDue)}
                          </>
                        )}
                      </Button>

                      <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 pt-1">
                        <Lock className="h-3 w-3" /> Secure payment powered by Stripe
                      </div>
                    </div>
                  )}

                  {invoice.balanceDue > 0 && !invoice.onlinePaymentEnabled && (
                    <div className="px-6 py-5" data-testid="pay-online-unavailable">
                      <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3.5 text-[12.5px] text-amber-800 flex items-start gap-2">
                        <Lock className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                          Online payments aren't set up for this business yet. Please contact them to pay by another
                          method.
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-4">
          <ShieldCheck className="h-3.5 w-3.5" /> Your card details are processed securely by Stripe.
        </div>
        <PoweredBy dark className="mt-3" />
      </div>
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[12.5px] text-slate-500">{label}</span>
      <span className={`text-[13px] ${strong ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>
        {value}
      </span>
    </div>
  );
}
